<template>
  <div class="job-type-manage-page">
    <header class="jtm-header">
      <div>
        <h2 class="jtm-title">직종관리</h2>
        <p class="jtm-sub">권한 100 전용 · 카이온 전역 직종 마스터 (인사관리·사원등록 선택 목록)</p>
      </div>
      <button type="button" class="jtm-btn jtm-btn--primary" :disabled="saving" @click="openCreate">
        직종 추가
      </button>
    </header>

    <section class="jtm-toolbar">
      <label class="jtm-check">
        <input v-model="showInactive" type="checkbox" @change="loadList" />
        비활성 포함
      </label>
      <button type="button" class="jtm-btn jtm-btn--ghost" :disabled="loading" @click="loadList">
        새로고침
      </button>
    </section>

    <section class="jtm-card">
      <div v-if="loading" class="jtm-empty">불러오는 중…</div>
      <div v-else-if="!rows.length" class="jtm-empty">등록된 직종이 없습니다.</div>
      <table v-else class="jtm-table">
        <thead>
          <tr>
            <th class="col-sort">순서</th>
            <th>직종명</th>
            <th class="col-flag">생산직</th>
            <th class="col-usage">사용</th>
            <th class="col-status">상태</th>
            <th class="col-actions">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.cjt_idx" :class="{ inactive: row.cjt_status !== 'Y' }">
            <td class="col-sort">{{ row.cjt_sort }}</td>
            <td class="col-name">{{ row.cjt_name }}</td>
            <td class="col-flag">
              <span v-if="row.cjt_is_production" class="jtm-badge jtm-badge--prod">대상</span>
              <span v-else class="jtm-muted">—</span>
            </td>
            <td class="col-usage">{{ row.usageCount ?? 0 }}명</td>
            <td class="col-status">
              <span class="jtm-badge" :class="row.cjt_status === 'Y' ? 'jtm-badge--on' : 'jtm-badge--off'">
                {{ row.cjt_status === 'Y' ? '사용' : '비활성' }}
              </span>
            </td>
            <td class="col-actions">
              <button type="button" class="jtm-link" @click="openEdit(row)">수정</button>
              <button
                v-if="row.cjt_status === 'Y'"
                type="button"
                class="jtm-link jtm-link--danger"
                @click="deactivate(row)"
              >
                비활성
              </button>
              <button
                v-else
                type="button"
                class="jtm-link"
                @click="reactivate(row)"
              >
                복구
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <div v-if="dialogOpen" class="jtm-modal-backdrop" @click.self="closeDialog">
      <div class="jtm-modal">
        <h3 class="jtm-modal-title">{{ editing ? '직종 수정' : '직종 추가' }}</h3>
        <label class="jtm-label">직종명 <em>*</em></label>
        <input v-model="form.name" class="jtm-input" maxlength="50" placeholder="예: 요양보호사" />
        <label class="jtm-label">정렬 순서</label>
        <input v-model.number="form.sort" class="jtm-input" type="number" />
        <label class="jtm-check jtm-check--block">
          <input v-model="form.isProduction" type="checkbox" />
          생산직 비과세 대상 직종
        </label>
        <div class="jtm-modal-actions">
          <button type="button" class="jtm-btn jtm-btn--ghost" :disabled="saving" @click="closeDialog">취소</button>
          <button type="button" class="jtm-btn jtm-btn--primary" :disabled="saving || !form.name.trim()" @click="save">
            {{ saving ? '저장 중…' : '저장' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { jobTypeAdminApi } from '@/services/api';
import { loadJobTypes } from '@/composables/useJobTypes';

interface JobTypeRow {
  cjt_idx: number;
  cjt_name: string;
  cjt_sort: number;
  cjt_is_production: boolean;
  cjt_status: string;
  usageCount?: number;
}

const rows = ref<JobTypeRow[]>([]);
const loading = ref(false);
const saving = ref(false);
const showInactive = ref(true);
const dialogOpen = ref(false);
const editing = ref<JobTypeRow | null>(null);
const form = reactive({
  name: '',
  sort: 100,
  isProduction: false,
});

const loadList = async () => {
  loading.value = true;
  try {
    const res = await jobTypeAdminApi.list(showInactive.value);
    rows.value = res.data?.data || [];
  } catch (e: any) {
    alert(e.response?.data?.message || e.message || '목록 조회 실패');
    rows.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editing.value = null;
  form.name = '';
  form.sort = (rows.value.filter((r) => r.cjt_status === 'Y').length + 1) * 10;
  form.isProduction = false;
  dialogOpen.value = true;
};

const openEdit = (row: JobTypeRow) => {
  editing.value = row;
  form.name = row.cjt_name;
  form.sort = row.cjt_sort;
  form.isProduction = !!row.cjt_is_production;
  dialogOpen.value = true;
};

const closeDialog = () => {
  if (saving.value) return;
  dialogOpen.value = false;
};

const save = async () => {
  const name = form.name.trim();
  if (!name) {
    alert('직종명을 입력해 주세요.');
    return;
  }
  saving.value = true;
  try {
    if (editing.value) {
      const res = await jobTypeAdminApi.update(editing.value.cjt_idx, {
        name,
        sort: form.sort,
        isProduction: form.isProduction,
      });
      alert(res.data?.message || '수정되었습니다.');
    } else {
      await jobTypeAdminApi.create({
        name,
        sort: form.sort,
        isProduction: form.isProduction,
      });
      alert('등록되었습니다.');
    }
    dialogOpen.value = false;
    await loadList();
    await loadJobTypes(true);
  } catch (e: any) {
    alert(e.response?.data?.message || e.message || '저장 실패');
  } finally {
    saving.value = false;
  }
};

const deactivate = async (row: JobTypeRow) => {
  const usage = row.usageCount || 0;
  const msg = usage
    ? `"${row.cjt_name}"을(를) 비활성화할까요?\n사용 중인 사원 ${usage}명이 있습니다. 기존 데이터는 유지됩니다.`
    : `"${row.cjt_name}"을(를) 비활성화할까요?`;
  if (!confirm(msg)) return;
  saving.value = true;
  try {
    const res = await jobTypeAdminApi.remove(row.cjt_idx);
    alert(res.data?.message || '비활성화했습니다.');
    await loadList();
    await loadJobTypes(true);
  } catch (e: any) {
    alert(e.response?.data?.message || e.message || '비활성화 실패');
  } finally {
    saving.value = false;
  }
};

const reactivate = async (row: JobTypeRow) => {
  saving.value = true;
  try {
    await jobTypeAdminApi.update(row.cjt_idx, { status: 'Y' });
    alert('복구했습니다.');
    await loadList();
    await loadJobTypes(true);
  } catch (e: any) {
    alert(e.response?.data?.message || e.message || '복구 실패');
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadList();
});
</script>

<style lang="scss">
@import '@/assets/styles/pages/job-type-manage';
</style>
